import React, { useRef, useState, useEffect } from 'react';
import styles from './InputComponents.module.css';

const SignatureInput = ({ question, value, error, onChange, onClear, isSubmitted }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas && value) {
            const ctx = canvas.getContext('2d');
            const img = new Image();
            img.onload = () => {
                ctx.drawImage(img, 0, 0);
                setHasSignature(true);
            };
            img.src = value;
        }
    }, [value]);

    const startDrawing = (e) => {
        if (isSubmitted) return;
        setIsDrawing(true);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    };

    const draw = (e) => {
        if (!isDrawing || isSubmitted) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.stroke();
        setHasSignature(true);
    };

    const stopDrawing = async () => {
        if (!isDrawing) return;
        setIsDrawing(false);
        const canvas = canvasRef.current;
        const dataUrl = canvas.toDataURL();

        setIsUploading(true);
        try {
            await onChange(dataUrl);
        } catch (error) {
            console.error('Failed to upload signature:', error);
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            setHasSignature(false);
        } finally {
            setIsUploading(false);
        }
    };

    const clearSignature = async () => {
        if (isSubmitted || isUploading) return;

        setIsUploading(true);
        try {
            await onClear();
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            setHasSignature(false);
        } catch (error) {
            console.error('Failed to delete signature:', error);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className={styles.questionWrapper}>
            <label className={styles.questionLabel}>
                {question.text}
                {question.required && <span className={styles.required}>*</span>}
            </label>
            {question.annotation && (
                <span className={styles.annotation}>{question.annotation}</span>
            )}
            <div className={styles.signatureContainer}>
                <canvas
                    ref={canvasRef}
                    width={500}
                    height={200}
                    className={styles.signatureCanvas}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    style={{
                        border: error ? '2px solid red' : '1px solid #ccc',
                        opacity: isUploading ? 0.5 : 1,
                        pointerEvents: isUploading ? 'none' : 'auto'
                    }}
                />
                {!isSubmitted && (
                    <button
                        type="button"
                        onClick={clearSignature}
                        className={styles.clearButton}
                        disabled={!hasSignature || isUploading}
                    >
                        {isUploading ? 'Processing...' : 'Clear Signature'}
                    </button>
                )}
            </div>
            {error && <span className={styles.errorText}>{error}</span>}
        </div>
    );
};

export default SignatureInput;